import cx from "classnames";
import {
	type ForwardRefRenderFunction,
	type MouseEventHandler,
	type ReactNode,
	forwardRef,
} from "react";

import { ChevronDownIcon } from "@moment/icons";

import { MomentButton, type MomentButtonProps } from "../MomentButton";

export type AppTopBarButtonProps = {
	variant?: MomentButtonProps["variant"];
	level?: MomentButtonProps["level"];
	className?: string;
	style?: "action" | "dropdown";
	disabled?: boolean;
	maximizeHitArea?: boolean;
	onClick?: MouseEventHandler<HTMLButtonElement>;
	children: ReactNode;
};

const BaseAppTopBarButton: ForwardRefRenderFunction<HTMLButtonElement, AppTopBarButtonProps> = (
	{
		children,
		variant = "normal",
		level = "tertiary",
		className,
		style,
		disabled,
		maximizeHitArea = true,
		onClick,
		// IMPORTANT: Props spread allows radix-ui to be able to use this in `Trigger`.
		...props
	},
	ref
) => {
	return (
		<div className="flex h-full min-w-0 items-center">
			<MomentButton
				ref={ref}
				className={cx(
					{ "hit-maximizer hit-maximizer--bar": maximizeHitArea },
					"relative",
					"flex",
					"flex-row",
					level === "primary" ? "!px-3" : "!px-2",
					className,
					// remove unneeded button styles
					"!bg-transparent",
					"!border-none",
					"!shadow-none",
					"!outline-none",
					"!ring-0"
				)}
				variant={variant}
				level={level}
				onClick={onClick}
				disabled={disabled}
				{...props}
			>
				{children}
				{style && <ChevronDownIcon size={16} className="scale-50" />}
			</MomentButton>
		</div>
	);
};

export const AppTopBarButton = forwardRef(BaseAppTopBarButton);
